import { Link } from 'react-router-dom'
import Logo from '../components/Logo'
import PublicShell from '../components/PublicShell'
import { FEE_CALCULATOR_PATH, VERIFY_PATH } from '../routes'

/**
 * Signed-out "/": what FinCreator is and where to check it. Deliberately no
 * performance figures, win rates, R, returns, testimonials or claims of
 * profitability or edge (legal review pending): it points to the pages that
 * already show the record and its fingerprints, and says the engine is under
 * evaluation.
 */
export const ENGINE_STATUS_LINE =
  'The signal engine is under evaluation and has not been shown to have an edge. Nothing here is financial advice.'

const STEPS: Array<[string, string]> = [
  [
    'A signal is published before its outcome',
    'The engine scans the market and publishes each call — entry, stop and target — before anyone knows how it ends.',
  ],
  [
    'Every call is fingerprinted',
    'Each published call is fingerprinted and the fingerprints are published daily, so anyone can check that a call was not changed after the fact.',
  ],
  [
    'Every close is scored in full',
    'When a call closes it is scored a win or a loss on its price move and stays on the record, losses included. Creators can post from that record.',
  ],
]

const Landing = () => (
  <PublicShell>
    <section className="py-6" data-testid="landing">
      <h1>
        <Logo size="xl" />
      </h1>
      <p className="mt-3 max-w-2xl text-base text-gray-600 dark:text-gray-300">
        An AI signal engine whose every call is published before the outcome, fingerprinted, and scored win or loss in
        full — and tools for creators to post from that record.
      </p>
      <p
        className="mt-4 max-w-2xl rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm text-amber-700 dark:text-amber-200"
        data-testid="landing-status"
      >
        {ENGINE_STATUS_LINE}
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <Link
          to="/register"
          className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-terminal-bg hover:bg-accent-hover"
        >
          Create an account
        </Link>
        <Link
          to="/login"
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-terminal-border dark:text-gray-200 dark:hover:bg-white/5"
        >
          Sign in
        </Link>
        <Link
          to="/learn"
          className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 underline-offset-2 hover:underline dark:text-gray-200"
          data-testid="landing-courses-link"
        >
          Browse courses
        </Link>
      </div>
    </section>

    <section className="py-6" aria-labelledby="how-it-works">
      <h2 id="how-it-works" className="text-xl font-semibold text-gray-900 dark:text-gray-100">
        How it works
      </h2>
      <ol className="mt-4 grid gap-4 md:grid-cols-3">
        {STEPS.map(([title, body], index) => (
          <li
            key={title}
            className="rounded-lg border border-gray-200 bg-white p-4 dark:border-terminal-border dark:bg-terminal-surface"
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Step {index + 1}</p>
            <h3 className="mt-1 font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{body}</p>
          </li>
        ))}
      </ol>
    </section>

    <section className="py-6" aria-labelledby="check-it">
      <h2 id="check-it" className="text-xl font-semibold text-gray-900 dark:text-gray-100">
        Check it yourself
      </h2>
      <ul className="mt-3 space-y-2 text-sm">
        <li>
          <Link to={FEE_CALCULATOR_PATH} className="text-accent hover:underline">
            See every closed signal
          </Link>{' '}
          <span className="text-gray-500">— each close, win or loss, before fees, with your own fee if you enter one.</span>
        </li>
        <li>
          <Link to={VERIFY_PATH} className="text-accent hover:underline">
            Check the fingerprints
          </Link>{' '}
          <span className="text-gray-500">— match any published call against its daily fingerprint.</span>
        </li>
      </ul>
    </section>
  </PublicShell>
)

export default Landing
